/**
 * The "this copy of the app has been modified" screen.
 *
 * Plain DOM, not a Vue component: it has to work before the app mounts and
 * regardless of what state the Vue tree is in, and it replaces everything in
 * <body> so no app UI (including Ionic overlays appended later) is usable
 * underneath it. JavaScript keeps running on purpose — Capgo's notifyAppReady
 * still fires, so a device blocked by a false positive still receives the OTA
 * bundle that fixes it.
 */

export const BLOCK_SCREEN_ID = 'hogc-tamper-block';

const STORE_NAME = {
  ios: 'the App Store',
  android: 'the Google Play Store',
};

export function blockScreenMessage(platform) {
  const store = STORE_NAME[platform] || 'the App Store / Play Store';
  return `This copy of the app has been modified. Please reinstall HOGC One from ${store}.`;
}

/**
 * Show the block screen. Idempotent: a second threat does not stack screens.
 * @param {{ platform?: string, doc?: Document }} options
 */
export function showTamperBlockScreen({ platform, doc = globalThis.document } = {}) {
  if (!doc || !doc.body) return false;
  if (doc.getElementById(BLOCK_SCREEN_ID)) return true;

  const screen = doc.createElement('div');
  screen.id = BLOCK_SCREEN_ID;
  screen.setAttribute('role', 'alertdialog');
  screen.setAttribute('aria-modal', 'true');
  screen.setAttribute('aria-labelledby', `${BLOCK_SCREEN_ID}-title`);
  screen.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'padding:32px 24px',
    'background:#ffffff',
    'color:#1a1a1a',
    'text-align:center',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
  ].join(';');

  const title = doc.createElement('h1');
  title.id = `${BLOCK_SCREEN_ID}-title`;
  title.textContent = 'App cannot be opened';
  title.style.cssText = 'font-size:22px;font-weight:700;margin:0 0 12px';

  const body = doc.createElement('p');
  body.textContent = blockScreenMessage(platform);
  body.style.cssText = 'font-size:16px;line-height:1.5;margin:0;max-width:320px';

  screen.append(title, body);
  doc.body.replaceChildren(screen);
  return true;
}
